import { LowerTeethDivider, ToothNamesByNumbers, UpperTeethDivider } from 'app/core/constants';
import { DentalNotation, ToothApplianceType, ToothConnectorType } from 'app/core/enums';
import { getToothTitle } from 'app/core/helpers';

export class ToothNote {
  teeth: number;
  notes: string;
  implantDiameter?: number | undefined;
  useMaxDiameter: boolean;
  implantLength?: number | undefined;
  useMaxLength: boolean;
  toothApplianceType?: ToothApplianceType | undefined;
  groupNumber?: number | undefined;

  constructor(
    tooth: number,
    note: string,
    implantDiameter: number,
    implantLength: number,
    toothApplianceType: ToothApplianceType | undefined,
    useMaxDiameter: boolean = true,
    useMaxLength: boolean = true,
    groupNumber: number | undefined
  ) {
    this.notes = note;
    this.teeth = tooth;

    this.implantDiameter = implantDiameter;
    this.implantLength = implantLength;
    this.useMaxDiameter = useMaxDiameter;
    this.useMaxLength = useMaxLength;

    this.toothApplianceType = toothApplianceType;
    this.groupNumber = groupNumber;
  }
}

export class Tooth {
  private _toothApplianceType?: ToothApplianceType | undefined;

  index: number;
  notes: string;
  implantDiameter?: number;
  useMaxDiameter: boolean;
  implantLength?: number;
  useMaxLength: boolean;

  groupNumber?: number | undefined;
  isSelected: boolean;

  cssClass: string;
  cssToothClass: string;
  title: string | number;

  connector!: ITeethConnector;
  nextIndex: number = 0;

  public set toothApplianceType(value: ToothApplianceType | undefined) {
    this._toothApplianceType = value;

    this.cssClass = this.getClass();
  }

  public get toothApplianceType(): ToothApplianceType | undefined {
    return this._toothApplianceType;
  }

  constructor(
    tooth: number,
    note: string,
    implantDiameter: number | undefined,
    implantLength: number | undefined,
    toothApplianceType: ToothApplianceType | undefined,
    useMaxDiameter: boolean = true,
    useMaxLength: boolean = true,
    groupNumber: number | undefined,
    dentalNotation: number = DentalNotation.Utn
  ) {
    this.notes = note;
    this.index = tooth;

    this.implantDiameter = implantDiameter;
    this.implantLength = implantLength;
    this.useMaxDiameter = useMaxDiameter;
    this.useMaxLength = useMaxLength;

    this.toothApplianceType = toothApplianceType;
    this.groupNumber = groupNumber;

    this.isSelected = false;
    this.cssClass = this.getClass();
    this.cssToothClass = this.getToothClass();

    if (tooth !== LowerTeethDivider && tooth !== UpperTeethDivider) {
      this.connector = {
        type: ToothConnectorType.PermanentClosed,
        isActive: false,
      };
    }

    if (tooth < UpperTeethDivider) {
      this.nextIndex = tooth + 1;
    } else if (tooth > LowerTeethDivider) {
      this.nextIndex = tooth - 1;
    }

    this.title = getToothTitle(tooth, dentalNotation);
  }

  private getClass(): string {
    let className: string = 'tooth-v2-';

    if (this._toothApplianceType === ToothApplianceType.PonticSites) {
      className += 'base-';
    }

    className += this.index <= UpperTeethDivider ? 'upper-' : 'lower-';

    for (let i = 0; i < ToothNamesByNumbers.length; i++) {
      if (ToothNamesByNumbers[i].toothNumbers.some((n: number) => n === this.index)) {
        className += ToothNamesByNumbers[i].toothName;
        break;
      }
    }

    className += this.index > 8 && this.index < 25 ? ' fliped' : '';

    return className;
  }

  private getToothClass(): string {
    let className: string = this.index <= UpperTeethDivider ? 'upper-' : 'lower-';

    for (let i = 0; i < ToothNamesByNumbers.length; i++) {
      if (ToothNamesByNumbers[i].toothNumbers.some((n: number) => n === this.index)) {
        className += ToothNamesByNumbers[i].toothName;
        break;
      }
    }

    return className;
  }

  public isSameArch(compareTooth: Tooth): boolean {
    return !(
      (this.index <= UpperTeethDivider && compareTooth.index > UpperTeethDivider) ||
      (this.index > UpperTeethDivider && compareTooth.index <= UpperTeethDivider)
    );
  }
}

export interface ITeethConnector {
  type: ToothConnectorType;
  isActive: boolean;
}

export class RestorationsToothNote extends ToothNote {
  restorationsSubTypes?: number;

  constructor(
    tooth: number,
    note: string,
    implantDiameter: number,
    implantLength: number,
    toothApplianceType: ToothApplianceType | undefined,
    useMaxDiameter: boolean = true,
    useMaxLength: boolean = true,
    groupNumber: number,
    restorationsSubTypes: number
  ) {
    super(tooth, note, implantDiameter, implantLength, toothApplianceType, useMaxDiameter, useMaxLength, groupNumber);
    this.restorationsSubTypes = restorationsSubTypes;
  }
}

export class RestorationsTooth extends Tooth {
  restorationsSubTypes?: number;
  toothClass?: any;
  toothSubClass?: any;
  color?: string;
  isLastTooth?: boolean;
  isFirstTooth?: boolean;
  allowChangeApplianceType?: boolean;

  constructor(
    tooth: number,
    note: string,
    implantDiameter: number,
    implantLength: number,
    toothApplianceType: ToothApplianceType | undefined,
    useMaxDiameter: boolean = true,
    useMaxLength: boolean = true,
    groupNumber: number,
    dentalNotation: number = DentalNotation.Utn,
    restorationsSubTypes: number,
    toothClass: any = null,
    toothSubClass: any = null,
    color: string = '',
    isLastTooth: boolean = false,
    isFirstTooth: boolean = false,
    allowChangeApplianceType: boolean = false
  ) {
    super(
      tooth,
      note,
      implantDiameter,
      implantLength,
      toothApplianceType,
      useMaxDiameter,
      useMaxLength,
      groupNumber,
      dentalNotation
    );
    this.restorationsSubTypes = restorationsSubTypes;
    this.toothClass = toothClass;
    this.toothSubClass = toothSubClass;
    this.color = color;
    this.isLastTooth = isLastTooth;
    this.isFirstTooth = isFirstTooth;
    this.allowChangeApplianceType = allowChangeApplianceType;
  }
}
